/**
 * GamepadController - Polls gamepad state and triggers callbacks
 *
 * Uses the Gamepad API to support:
 * - Left stick / D-pad: Pan the map
 * - Triggers (L2/R2): Zoom in/out
 * - R1: Toggle couch mode (hide/show UI)
 * - A button: Toggle demo mode
 * - B button: Toggle frame stats overlay
 *
 * On Android TV, also listens for custom 'androidButton' events dispatched
 * from native code, since the WebView doesn't receive all gamepad events.
 */

export interface GamepadCallbacks {
  onPan(dx: number, dy: number, deltaTime: number): void;
  onZoom(delta: number, deltaTime: number): void;
  onToggleUI(): void;
  onToggleDemo(): void;
  onToggleStats(): void;
  onToggleAA(): void;
}

/** Android TV provides analog axes via this global (set by native code) */
interface AndroidAxes {
  lx: number;
  ly: number;
  rx: number;
  ry: number;
  lt: number;
  rt: number;
}

declare global {
  interface Window {
    androidAxes?: AndroidAxes;
  }
}

export class GamepadController {
  private callbacks: GamepadCallbacks;

  // Button state tracking for edge detection (prevent repeat triggers)
  private uiTogglePressed = false;
  private aPressed = false;
  private bPressed = false;
  private xPressed = false;

  // Bound handler for Android button events
  private androidButtonHandler: (e: Event) => void;

  constructor(callbacks: GamepadCallbacks) {
    this.callbacks = callbacks;
    this.androidButtonHandler = this.handleAndroidButton.bind(this);
  }

  /**
   * Start listening for Android button events.
   * Call pollOnce() from your render loop instead of using the internal loop.
   */
  start(): void {
    // Listen for Android TV button events (dispatched from native WebView code)
    window.addEventListener('androidButton', this.androidButtonHandler);
  }

  stop(): void {
    window.removeEventListener('androidButton', this.androidButtonHandler);
  }

  /**
   * Poll gamepad state once. Call this from your main render loop.
   */
  pollOnce(deltaTime: number): void {
    // Check for Android TV analog axes (set by native code)
    const androidAxes = window.androidAxes;
    if (androidAxes) {
      this.processAndroidAxes(androidAxes, deltaTime);
    }

    // Also check standard Gamepad API (for desktop/browser)
    const gamepads = navigator.getGamepads();
    const gp = gamepads[0] ?? gamepads[1] ?? gamepads[2] ?? gamepads[3];

    if (gp) {
      this.processGamepad(gp, deltaTime);
    }
  }

  /**
   * Handle button events from Android TV native code.
   * These are dispatched as CustomEvent with detail containing the button name.
   */
  private handleAndroidButton(e: Event): void {
    const button = (e as CustomEvent<string>).detail;
    // Fixed delta for discrete button presses (simulate ~60fps frame)
    const fixedDelta = 1 / 60;

    switch (button) {
      case 'buttonA':
        this.callbacks.onToggleDemo();
        break;
      case 'buttonB':
        this.callbacks.onToggleStats();
        break;
      case 'back':
        // Back button could stop demo or go back
        this.callbacks.onToggleDemo();
        break;
      case 'buttonR1':
        this.callbacks.onToggleUI();
        break;
      case 'buttonL1':
        // Could be used for something else
        break;
      case 'buttonL2':
        // Zoom out
        this.callbacks.onZoom(-1, fixedDelta);
        break;
      case 'buttonR2':
        // Zoom in
        this.callbacks.onZoom(1, fixedDelta);
        break;
      case 'buttonX':
        this.callbacks.onToggleAA();
        break;
      case 'buttonY':
      case 'buttonSelect':
      case 'buttonStart':
      case 'menu':
        // Reserved for future use
        break;
    }
  }

  /**
   * Process analog axes from Android TV native code.
   * Provides smooth, pressure-sensitive input for sticks and triggers.
   */
  private processAndroidAxes(axes: AndroidAxes, deltaTime: number): void {
    // Left stick with deadzone (inverted for natural panning)
    const lx = this.applyDeadzone(-axes.lx);
    const ly = this.applyDeadzone(-axes.ly);

    if (lx !== 0 || ly !== 0) {
      this.callbacks.onPan(lx, ly, deltaTime);
    }

    // Triggers for zoom (pressure sensitive)
    const zoomDelta = axes.rt - axes.lt;
    if (Math.abs(zoomDelta) > 0.1) {
      this.callbacks.onZoom(zoomDelta, deltaTime);
    }
  }

  private processGamepad(gp: Gamepad, deltaTime: number): void {
    // Left stick (axes 0, 1)
    const lx = this.applyDeadzone(gp.axes[0] ?? 0);
    const ly = this.applyDeadzone(gp.axes[1] ?? 0);

    // D-pad (buttons 12-15: up, down, left, right on standard mapping)
    const dpadUp = gp.buttons[12]?.pressed ?? false;
    const dpadDown = gp.buttons[13]?.pressed ?? false;
    const dpadLeft = gp.buttons[14]?.pressed ?? false;
    const dpadRight = gp.buttons[15]?.pressed ?? false;

    const dpadX = (dpadRight ? 1 : 0) - (dpadLeft ? 1 : 0);
    const dpadY = (dpadDown ? 1 : 0) - (dpadUp ? 1 : 0);

    // Combine stick and d-pad (prefer stick if both are used)
    // Invert both axes: push right = pan right (map moves left), push up = pan up (map moves down)
    const panX = lx !== 0 ? -lx : -dpadX;
    const panY = ly !== 0 ? -ly : -dpadY;

    if (panX !== 0 || panY !== 0) {
      this.callbacks.onPan(panX, panY, deltaTime);
    }

    // Triggers (buttons 6 = L2, 7 = R2 on standard mapping)
    // These have analog values (.value) from 0-1
    const lt = gp.buttons[6]?.value ?? 0;
    const rt = gp.buttons[7]?.value ?? 0;
    const zoomDelta = rt - lt;

    if (Math.abs(zoomDelta) > 0.1) {
      this.callbacks.onZoom(zoomDelta, deltaTime);
    }

    // R1 (button 5) - toggle UI (edge triggered)
    const r1 = gp.buttons[5]?.pressed ?? false;
    if (r1 && !this.uiTogglePressed) {
      this.callbacks.onToggleUI();
    }
    this.uiTogglePressed = r1;

    // A button (button 0) - toggle demo (edge triggered)
    const a = gp.buttons[0]?.pressed ?? false;
    if (a && !this.aPressed) {
      this.callbacks.onToggleDemo();
    }
    this.aPressed = a;

    // B button (button 1) - toggle stats (edge triggered)
    const b = gp.buttons[1]?.pressed ?? false;
    if (b && !this.bPressed) {
      this.callbacks.onToggleStats();
    }
    this.bPressed = b;

    // X button (button 2) - toggle AA (edge triggered)
    const x = gp.buttons[2]?.pressed ?? false;
    if (x && !this.xPressed) {
      this.callbacks.onToggleAA();
    }
    this.xPressed = x;
  }

  /**
   * Apply deadzone to analog stick input to prevent drift
   */
  private applyDeadzone(value: number, deadzone = 0.15): number {
    if (Math.abs(value) < deadzone) return 0;
    // Scale the remaining range to 0-1
    const sign = Math.sign(value);
    const magnitude = Math.abs(value);
    return sign * ((magnitude - deadzone) / (1 - deadzone));
  }
}
